/**
 * Keep the cart drawer in sync after the page is restored from cache.
 *
 * Shopify caches storefront page HTML. When a shopper adds items (via AJAX),
 * goes to checkout, then presses Back, the browser serves the *cached* page
 * HTML — whose cart drawer reflects the OLD cart (often empty) — even though
 * the real, server-side cart is unchanged. The theme only re-renders the drawer
 * in response to live cart mutations (cartLinesUpdate), so nothing corrects the
 * stale drawer on this cached page view: the shopper sees their items vanish
 * until they manually refresh. (cart-icon.js re-syncs the count bubble, but only
 * within a 10s window that has usually expired by the time checkout is done, and
 * it never touches the line items.)
 *
 * Fix: whenever the page is shown, compare the drawer's displayed item count
 * against the authoritative count that cart-icon.js stores in sessionStorage on
 * every mutation. If they disagree (or the page came back via bfcache and we
 * can't tell), re-render the cart drawer section from the server — which is
 * served `private, no-store`, so it is always the live cart — and morph it in.
 * No visible reload; the drawer simply shows the correct contents.
 */
import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'cart-drawer-section';

/**
 * The authoritative cart item count cart-icon.js persists on every mutation.
 * @returns {number | null} the count, or null when it isn't known.
 */
function authoritativeCount() {
  try {
    const raw = sessionStorage.getItem('cart-count');
    if (!raw) return null;
    const count = parseInt(JSON.parse(raw)?.value, 10);
    return Number.isNaN(count) ? null : count;
  } catch {
    return null;
  }
}

/**
 * The item count the drawer is currently showing (0 when it shows the empty state).
 * @param {Element} drawer
 * @returns {number}
 */
function displayedCount(drawer) {
  if (drawer.querySelector('[data-cart-drawer-empty]')) return 0;
  const count = parseInt(drawer.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
  return Number.isNaN(count) ? 0 : count;
}

/**
 * Re-render the cart drawer section from the server and morph it into the DOM.
 * @param {Element} drawer
 */
function resyncDrawer(drawer) {
  // Match the theme's own behaviour: only pull in the section stylesheet when
  // coming from the empty state (empty -> items adds the summary markup).
  const injectStylesheet = displayedCount(drawer) === 0;
  sectionRenderer
    .renderSection(SECTION_ID, { cache: false, mode: 'hydration', injectStylesheet })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer-restore] re-sync failed:', error);
    });
}

/**
 * Keep the header cart bubble consistent with the drawer. cart-icon.js only
 * self-corrects the bubble within a 10s window, which has usually expired by
 * the time the shopper returns from checkout — so on a stale restore the badge
 * can read 0 while the drawer (now re-synced) shows items.
 * @param {number} count
 */
function syncCartBubble(count) {
  const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
  if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
    cartIcon.renderCartBubble(count, false);
  }
}

/**
 * @param {boolean} persisted - whether the page was restored from bfcache.
 */
function reconcile(persisted) {
  const drawer = document.querySelector('#cart-drawer');
  if (!drawer) return;

  const truth = authoritativeCount();
  const stale = truth !== null ? truth !== displayedCount(drawer) : persisted;

  if (!stale) return;

  resyncDrawer(drawer);
  if (truth !== null) syncCartBubble(truth);
}

// Run once now — covers back-navigations served as a fresh load from the HTTP
// cache (the common checkout -> Back case; pageshow there fires persisted=false).
reconcile(false);

// And on every subsequent page show — covers bfcache restores (persisted=true).
window.addEventListener('pageshow', (event) => reconcile(event.persisted));
